// client/src/components/ProductOfTheMonth.jsx
// Featured product of the month showcase

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Badge, Row, Col } from 'react-bootstrap';
import { useCart } from './CartContext';
import { FaStar, FaShoppingCart, FaTrophy, FaArrowRight } from 'react-icons/fa';
import { getImageUrl, getPlaceholderImage } from '../config';
import PriceDisplay from './PriceDisplay';
import './ProductOfTheMonth.css';

const ProductOfTheMonth = ({ product, discount = 20, variantId = null }) => {
  const navigate = useNavigate();
  const cartContext = useCart();
  const addToCart = cartContext?.addToCart || (() => {});

  // Track which variant user is currently viewing
  const [viewingVariantId, setViewingVariantId] = useState(variantId);

  if (!product) return null;

  // Find the selected variant (from admin config)
  const selectedVariant = variantId && product.variants
    ? product.variants.find(v => v._id === variantId)
    : null;

  // Find the variant user is currently viewing
  const viewingVariant = viewingVariantId && product.variants
    ? product.variants.find(v => v._id === viewingVariantId)
    : selectedVariant;

  // Get effective price: use variant price if available, otherwise product price
  const getEffectivePrice = () => {
    if (viewingVariant && viewingVariant.price) {
      return viewingVariant.price;
    }
    return product.price;
  };

  const handleAddToCart = () => {
    if (addToCart) {
      addToCart({
        ...product,
        ...(viewingVariantId && { variantId: viewingVariantId }), // Include the variant being viewed
        price: getEffectivePrice(), // Use variant price if available
        quantity: 1,
      });
    }
  };

  const handleViewProduct = () => {
    navigate(`/product/${product._id}`);
  };

  return (
    <div className="product-of-month-wrapper">
      <Card className="product-of-month-card">
        <div className="month-badge-container">
          <Badge bg="warning" className="month-badge">
            <FaTrophy className="me-1" />
            PRODUCT OF THE MONTH
          </Badge>
        </div>

        <Row className="g-0">
          {/* Product Image */}
          <Col md={6} className="position-relative">
            <div className="product-image-container">
              <img
                src={getImageUrl(product.image)}
                alt={product.name}
                className="product-image"
                loading="lazy"
                onError={(e) => {
                  e.target.src = getPlaceholderImage('Product Image');
                }}
              />
              <div className="discount-badge">
                <span>{discount}%</span>
                <p>OFF</p>
              </div>
            </div>
          </Col>

          {/* Product Info */}
          <Col md={6}>
            <Card.Body className="product-info">
              <div className="rating-section mb-3">
                <div className="stars">
                  {product.rating && [...Array(Math.round(product.rating))].map((_, i) => (
                    <FaStar key={i} className="star-icon" />
                  ))}
                  {product.rating && [...Array(5 - Math.round(product.rating))].map((_, i) => (
                    <FaStar key={i + Math.round(product.rating)} className="star-icon" style={{ opacity: 0.3 }} />
                  ))}
                </div>
                <span className="rating-text">
                  ({product.reviews?.length || 0} {product.reviews?.length === 1 ? 'review' : 'reviews'})
                </span>
              </div>

              <h3 className="product-title">{product.name}</h3>

              {/* Variant Display */}
              {viewingVariant && (
                <div className="variant-badge-container mb-2">
                  <span className="variant-badge">
                    {viewingVariant.size && viewingVariant.color
                      ? `${viewingVariant.size} - ${viewingVariant.color}`
                      : viewingVariant.size || viewingVariant.color || 'Selected Variant'}
                  </span>
                </div>
              )}

              <p className="product-description">
                {viewingVariant && viewingVariant.description
                  ? viewingVariant.description
                  : product.shortDescription || product.description?.substring(0, 100)}
              </p>

              {/* Variant Selection */}
              {product.variants && product.variants.length > 0 && (
                <div className="variant-selector-container mb-3">
                  <small className="text-muted d-block mb-2">Select Variant:</small>
                  <div className="variant-buttons-group">
                    {product.variants.map((variant) => (
                      <button
                        key={variant._id}
                        className={`variant-option-btn ${viewingVariantId === variant._id ? 'active' : ''}`}
                        onClick={() => setViewingVariantId(variant._id)}
                        title={variant.size && variant.color
                          ? `${variant.size} - ${variant.color}`
                          : variant.size || variant.color || 'Variant'}
                      >
                        {variant.size && variant.color
                          ? `${variant.size}`
                          : variant.size || variant.color || 'V'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Price Section */}
              <div className="price-section mb-4">
                <div className="price">
                  <PriceDisplay
                    price={getEffectivePrice()}
                    className="product-of-month-price"
                    showGlobalDiscount={false}
                    showDiscountLabel={true}
                    customDiscountPercentage={discount}
                    customDiscountLabel={`Product of the Month ${discount}%`}
                  />
                </div>
              </div>

              {/* Highlights */}
              <ul className="highlights">
                <li>In Stock - Ships within 24 hours</li>
                <li>Free delivery over £750</li>
                <li>12-month warranty included</li>
                <li>Expert support available</li>
              </ul>

              {/* Action Buttons */}
              <div className="action-buttons">
                <Button
                  className="add-to-cart-btn"
                  onClick={handleAddToCart}
                >
                  <FaShoppingCart className="me-2" />
                  Add to Cart
                </Button>
                <Button
                  variant="outline-secondary"
                  className="view-details-btn"
                  onClick={handleViewProduct}
                >
                  View Details
                  <FaArrowRight className="ms-2" />
                </Button>
              </div>
            </Card.Body>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default ProductOfTheMonth;
